import React, { useCallback, useMemo, forwardRef } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet, { BottomSheetView, BottomSheetBackdrop, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import BottomSheetHeader from './BottomSheetHeader';
import PrimaryButton from './PrimaryButton';
import TertiaryButton from './TertiaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

interface FilterDrawerProps {
  isVisible: boolean;
  onClose: () => void;
  title: string;
  onApply: () => void;
  onClear: () => void;
  isApplyEnabled: boolean;
  children: React.ReactNode;
  type?: 'author' | 'period' | 'day' | 'time' | 'sentiment' | 'category' | 'default';
}

const FilterDrawer = forwardRef<BottomSheet, FilterDrawerProps>(
  ({ isVisible, onClose, title, onApply, onClear, isApplyEnabled, children, type = 'default' }, ref) => {
    const { t } = useLanguage();
    
    // Get screen dimensions and safe area insets
    const screenHeight = Dimensions.get('window').height;
    const screenWidth = Dimensions.get('window').width;
    const insets = useSafeAreaInsets();

    // Calculate dynamic height based on content and screen constraints
    const calculateContentHeight = () => {
      const headerHeight = 80; // Header with title and close button
      const footerHeight = 140; // Two buttons with padding
      const safeAreaBottom = Math.max(insets.bottom, 24); // Use actual safe area or minimum 24px

      // Maximum sheet height is 90% of screen height
      const maxSheetHeight = screenHeight * 0.90;

      // Calculate natural content height for different filters based on type prop
      let naturalContentHeight = 0;
      if (type === 'author') {
        naturalContentHeight = 2 * 68 + 8 + 32; // 2 options + gap + padding
      } else if (type === 'period') {
        naturalContentHeight = 3 * 68 + 16 + 32; // 3 options + gaps + padding
      } else if (type === 'day') {
        // Height for exactly 5 visible options with proper spacing
        const optionHeight = 68; // Height of each day option (padding + text + borders)
        const visibleOptions = 5;
        const gapsBetweenOptions = 8 * (visibleOptions - 1); // 8px gaps between 5 options
        naturalContentHeight = (optionHeight * visibleOptions) + gapsBetweenOptions + 32; // options + gaps + padding
      } else if (type === 'time') {
        // Height for exactly 5 rows of 3 columns with proper spacing
        const optionHeight = 68; // Height of each time option
        const visibleRows = 5;
        const gapsBetweenRows = 8 * (visibleRows - 1); // 8px gaps between 5 rows
        naturalContentHeight = (optionHeight * visibleRows) + gapsBetweenRows + 32; // options + gaps + padding
      } else if (type === 'sentiment') {
        // Height for exactly 5 visible options with proper spacing
        const optionHeight = 60; // Height of each sentiment option
        const visibleOptions = 5;
        const gapsBetweenOptions = 8 * (visibleOptions - 1); // 8px gaps between 5 options
        naturalContentHeight = (optionHeight * visibleOptions) + gapsBetweenOptions + 32; // options + gaps + padding
      } else if (type === 'category') {
        // Mirror the Day filter sizing so the sheet height matches the rest
        // of the filter family. The inner CategoryFilterContent ScrollView
        // handles overflow for the full category list.
        const optionHeight = 68;
        const visibleOptions = 5;
        const gapsBetweenOptions = 8 * (visibleOptions - 1);
        naturalContentHeight = (optionHeight * visibleOptions) + gapsBetweenOptions + 32;
      } else {
        naturalContentHeight = 200; // Default fallback
      }

      // Calculate total needed height for the sheet
      const totalNeededHeight = headerHeight + naturalContentHeight + footerHeight + safeAreaBottom;

      // Determine if we need scrolling and final height
      const needsScrolling = totalNeededHeight > maxSheetHeight;
      const finalHeight = needsScrolling ? maxSheetHeight : totalNeededHeight;

      return {
        totalHeight: finalHeight,
        maxHeight: maxSheetHeight,
        needsScrolling
      };
    };

    // Calculate snap points based on dynamic content height
    const { totalHeight: sheetHeight, needsScrolling: contentNeedsScrolling } = calculateContentHeight();
    const modalSnapPoints = useMemo(() => {
      const percentage = (sheetHeight / screenHeight) * 100;
      return [`${percentage}%`];
    }, [screenHeight, sheetHeight]);

    // Callbacks
    const handleSheetChanges = useCallback((index: number) => {
      if (index === -1) {
        onClose();
      }
    }, [onClose]);

    const renderBackdrop = useCallback(
      (props: any) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          opacity={0.4}
          enableTouchThrough={false}
          pressBehavior="close"
          style={[props.style, { height: screenHeight }]}
        />
      ),
      [screenHeight]
    );

    // Don't render the BottomSheet at all when not visible
    if (!isVisible) {
      return null;
    }

    return (
      <BottomSheet
        ref={ref}
        index={0}
        snapPoints={modalSnapPoints}
        onChange={handleSheetChanges}
        enablePanDownToClose
        enableDynamicSizing={false}
        backgroundStyle={styles.bottomSheetBackground}
        handleIndicatorStyle={styles.handleIndicator}
        backdropComponent={renderBackdrop}
        animateOnMount={true}
        android_keyboardInputMode="adjustResize"
        topInset={0}
        bottomInset={0}
        style={{ zIndex: 2000 }}
        enableContentPanningGesture={false}
        enableHandlePanningGesture={true}
        activeOffsetY={[-1, 1]}
        failOffsetX={[-5, 5]}
      >
        <BottomSheetView style={[styles.contentContainer, { height: sheetHeight }]}>
          {/* Header */}
          <View style={styles.headerSection}>
            <BottomSheetHeader 
              title={title} 
              onClose={onClose} 
            />
          </View>

          {/* Content */}
          {contentNeedsScrolling ? (
            <BottomSheetScrollView
              style={styles.scrollableContentSection}
              contentContainerStyle={styles.scrollableContent}
              showsVerticalScrollIndicator={true}
              indicatorStyle="default"
            >
              {children}
            </BottomSheetScrollView>
          ) : (
            <View style={styles.contentSection}>
              {children}
            </View>
          )}

          {/* Footer - Always visible at bottom */}
          <View style={[styles.footerSection, { paddingBottom: 24 }]}>
            <PrimaryButton 
              title={t('common.apply')} 
              onPress={onApply}
              disabled={!isApplyEnabled}
            />
            <TertiaryButton 
              title={t('common.clearFilters')} 
              onPress={onClear} 
            />
          </View>
        </BottomSheetView>
      </BottomSheet>
    );
  }
);

FilterDrawer.displayName = 'FilterDrawer';

export default FilterDrawer;

const styles = StyleSheet.create({
  bottomSheetBackground: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 24,
  },
  handleIndicator: {
    backgroundColor: '#DAD8CF',
    width: 40,
    height: 4,
  },
  contentContainer: {
    flex: 1,
  },
  headerSection: {
    flexShrink: 0, // Never shrink
  },
  contentSection: {
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 16,
    flex: 1, // Take available space but don't overflow
  },
  scrollableContentSection: {
    flex: 1, // Take all available space between header and footer
    paddingHorizontal: 24,
  },
  scrollableContent: {
    paddingTop: 8,
    paddingBottom: 16,
    flexGrow: 1, // Enable proper scrolling
  },
  footerSection: {
    flexShrink: 0, // Never shrink
    paddingHorizontal: 24,
    paddingTop: 0,
    gap: 16,
    backgroundColor: '#FFFFFF',
  },
});